import { useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft, CircleX, Globe, Info } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ConfirmPlanChangeModal } from "@/components/billing/ConfirmPlanChangeModal";
import { Alert, Button, ErrorState, FullPageSpinner, cx } from "@/components/ui";
import { missingPermissionReason } from "@/lib/access";
import { portal } from "@/lib/api";
import { choosePlanTarget } from "@/lib/billing";
import { formatMoney } from "@/lib/format";
import type { BillingPeriod, CustomerAccount, Plan, PlanKey } from "@/lib/types";

/** Figma "Billing — Plans (See plans · Compare plans)". */
export function ChoosePlanPage() {
  const { orgId, can, permissionsLoading } = useOrganization();
  const navigate = useNavigate();
  const plans = useQuery({ queryKey: ["plans"], queryFn: portal.plans, staleTime: 3_600_000 });
  const sub = useQuery({
    queryKey: ["subscription", orgId],
    queryFn: () => portal.subscription(orgId),
    enabled: can("billing:read"),
  });
  const [period, setPeriod] = useState<BillingPeriod | null>(null);
  const [target, setTarget] = useState<{ plan: PlanKey; period: BillingPeriod } | null>(null);

  if (permissionsLoading) return <FullPageSpinner />;
  if (!can("billing:read")) return <Alert tone="info">{missingPermissionReason("billing:read")}</Alert>;
  if (plans.isPending || sub.isPending) return <FullPageSpinner />;
  if (plans.isError) return <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />;
  if (sub.isError) return <ErrorState error={sub.error} onRetry={() => void sub.refetch()} />;

  const { account, payments_enabled } = sub.data;
  const shown = period ?? account.billing_period;
  const canManage = can("billing:manage");

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/billing"
        className="inline-flex items-center gap-1 self-start text-sm text-fg-subtle hover:text-fg"
      >
        <ChevronLeft className="size-4" aria-hidden /> Back
      </Link>
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-[30px] leading-9 font-semibold text-fg">Choose a plan</h1>
          <p className="mt-1 max-w-md text-sm text-[#766e6a]">
            You are on {account.plan.name}. Nothing is charged until you confirm on the next step.
          </p>
        </div>
        <div role="radiogroup" aria-label="Billing period" className="flex rounded-full bg-muted p-1">
          {(["monthly", "annual"] as const).map((p) => (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={shown === p}
              onClick={() => setPeriod(p)}
              className={cx(
                "rounded-full px-4 py-2 text-sm font-semibold",
                shown === p ? "bg-surface text-fg shadow-xs" : "text-fg-muted hover:text-fg",
              )}
            >
              {p === "monthly"
                ? "Monthly"
                : plans.data.annual_discount_percent > 0
                  ? `Annual · save ${plans.data.annual_discount_percent}%`
                  : "Annual"}
            </button>
          ))}
        </div>
      </div>

      {!payments_enabled && (
        <Alert tone="info">
          Online payments are coming soon. Until then, contact sales to move to a paid plan.
        </Alert>
      )}

      <div
        className={`grid gap-5 ${plans.data.plans.length >= 4 ? "md:grid-cols-2 xl:grid-cols-4" : plans.data.plans.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"}`}
      >
        {plans.data.plans.map((plan) => (
          <PlanCard
            key={plan.key}
            plan={plan}
            period={shown}
            account={account}
            disabledReason={
              !canManage
                ? missingPermissionReason("billing:manage")
                : !payments_enabled && plan.monthly_price_cents > 0
                  ? "Online payments are coming soon. Contact sales to move to a paid plan."
                  : undefined
            }
            onChoose={() => {
              const next = choosePlanTarget(account, plan, shown);
              if (next.kind === "checkout" || next.kind === "cancel") navigate(next.to);
              else if (next.kind === "schedule") setTarget({ plan: plan.key, period: shown });
            }}
          />
        ))}
      </div>

      <div className="grid gap-6 rounded-[14px] border border-border px-5 py-[18px] md:grid-cols-3">
        <Note icon={<Info className="size-[15px]" aria-hidden />} title="Nothing changes yet">
          You review the amount and the date on the next screen before anything is charged.
        </Note>
        <Note icon={<Globe className="size-[15px]" aria-hidden />} title="Domains are billed separately">
          A domain is paid to the registry on its own renewal date, whichever plan you are on.
        </Note>
        <Note icon={<Check className="size-[15px]" aria-hidden />} title="Change whenever you need">
          Move up and the new limits apply straight away. Move down and the change waits until the period you
          have paid for ends.
        </Note>
      </div>

      <ConfirmPlanChangeModal
        target={target}
        onClose={() => setTarget(null)}
        onDone={() => navigate("/billing", { replace: true })}
        provider={sub.data.payment_provider}
      />
    </div>
  );
}

function PlanCard({
  plan,
  period,
  account,
  disabledReason,
  onChoose,
}: {
  plan: Plan;
  period: BillingPeriod;
  account: CustomerAccount;
  /** Why this plan can't be chosen (permission, payments not switched on). */
  disabledReason?: string;
  onChoose: () => void;
}) {
  const isCurrentPlan = plan.key === account.plan.key;
  const isCurrent = isCurrentPlan && (period === account.billing_period || plan.monthly_price_cents === 0);
  const scheduled = account.scheduled_change;
  const isScheduled =
    scheduled?.plan.key === plan.key &&
    (scheduled.billing_period === period || plan.monthly_price_cents === 0);
  const annual = period === "annual" && plan.monthly_price_cents > 0;
  const perMonth = annual ? Math.round(plan.annual_price_cents / 12) : plan.monthly_price_cents;
  const label = isCurrent
    ? "Your current plan"
    : isScheduled
      ? "Already scheduled"
      : isCurrentPlan
        ? `Switch to ${period} billing`
        : `Choose ${plan.name}`;

  return (
    <div
      className={cx(
        "flex flex-col rounded-2xl bg-surface p-6",
        plan.recommended && !isCurrent ? "border-2 border-brand-500" : "border border-border",
      )}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <h2 className="text-xl font-semibold text-fg">{plan.name}</h2>
        {isCurrent ? (
          <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-fg-muted">
            Current plan
          </span>
        ) : (
          plan.recommended && (
            <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
              Most businesses choose this
            </span>
          )
        )}
      </div>
      <p className="mt-3 flex items-baseline gap-1.5">
        <span className="text-4xl font-semibold text-fg">
          {formatMoney(perMonth, plan.currency, { whole: true })}
        </span>
        <span className="text-sm text-[#766e6a]">/month</span>
      </p>
      {annual && (
        <p className="text-xs text-fg-subtle">
          {formatMoney(plan.annual_price_cents, plan.currency)} billed yearly + VAT
        </p>
      )}
      <p className="mt-1.5 text-sm text-fg-muted">{plan.tagline}</p>
      <ul className="mt-[18px] flex flex-1 flex-col">
        {plan.features.map((f) => (
          <li key={f.label} className="flex items-start gap-2.5 py-2 text-sm">
            {f.included ? (
              <Check className="mt-0.5 size-[15px] shrink-0 text-fg-muted" aria-label="Included" />
            ) : (
              <CircleX className="mt-0.5 size-[15px] shrink-0 text-fg-subtle" aria-label="Not included" />
            )}
            <span className={f.included ? "text-fg-muted" : "text-fg-subtle"}>{f.label}</span>
          </li>
        ))}
      </ul>
      <Button
        unstyled
        disabledReason={
          isCurrent
            ? "You're already on this plan."
            : isScheduled
              ? "This change is already scheduled."
              : disabledReason
        }
        onClick={onChoose}
        className={cx(
          "mt-5 w-full rounded-[10px] px-[18px] py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed",
          isCurrent || isScheduled
            ? "bg-muted text-[#b3a8a3]"
            : plan.recommended
              ? "bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-60"
              : "border border-border bg-surface text-[#443e3b] hover:bg-muted disabled:opacity-60",
        )}
      >
        {label}
      </Button>
    </div>
  );
}

function Note({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="flex items-center gap-2 text-sm font-semibold text-fg">
        {icon}
        {title}
      </p>
      <p className="text-xs text-[#766e6a]">{children}</p>
    </div>
  );
}
